import type { AxiosInstance } from 'axios';
import { isAxiosError } from 'axios';
import { config } from '@/config';
import { ChangePasswordRequest, PersonalClient } from '@/rest/apiv2.hsdesk';
import type {
  PanelInterfaceStyle,
  PanelThemeMode
} from '@/store/slices/themeSlice';

export interface DepartmentDto {
  id: string;
  name: string;
  description?: string;
}

export interface TeamDto {
  id: string;
  departmentId?: string;
  name: string;
  description?: string;
}

export interface PositionDto {
  id: string;
  name: string;
  description?: string;
}

export interface UserProfileRoleDto {
  roleId?: string;
  roleName?: string;
  description?: string;
  enabled?: boolean;
}

export interface UserProfileDto {
  id?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  image?: string;
  imageUrl?: string;
  deleteCurrentImage?: boolean;
  departmentId?: string;
  teamId?: string;
  positionIds?: string[];
  defaultPositionId?: string;
  signature?: string;
  emailConfirmed?: boolean;
  isActive?: boolean;
  roles?: UserProfileRoleDto[];
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  phoneNumber?: string;
  image?: string;
  deleteCurrentImage?: boolean;
  departmentId?: string;
  teamId?: string;
  positionIds: string[];
  defaultPositionId?: string;
  signature?: string;
}

export interface ProfileSettingsResponse {
  isMasterUser: boolean;
  isFullWidth: boolean;
  defaultView: string;
  density: string;
  language: string;
  timezone: string;
  dateFormat: string;
  availability: string;
  defaultPositionId?: string;
  quickActions: string[];
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  newOrderNotification: boolean;
  commentNotification: boolean;
  statusNotification: boolean;
  reminderNotification: boolean;
  newTenantEmailNotification: boolean;
  newTenantSmsNotification: boolean;
  newTenantInAppNotification: boolean;
  tenantUserEmailNotification: boolean;
  tenantUserSmsNotification: boolean;
  tenantUserInAppNotification: boolean;
  tenantProblemEmailNotification: boolean;
  tenantProblemSmsNotification: boolean;
  tenantProblemInAppNotification: boolean;
  dailySummary: boolean;
  showPhoneToTeam: boolean;
  showAvailability: boolean;
  requireConfirmForStatusChange: boolean;
  compactOrderCards: boolean;
  autoAssignCreatedOrders: boolean;
  panelInterfaceStyle: PanelInterfaceStyle;
  panelThemeMode: PanelThemeMode;
  panelPrimaryColor: string;
  panelBackgroundColor: string;
  panelSurfaceColor: string;
  panelTextColor: string;
  panelSidebarColor: string;
}

export const profileSettingsUpdatedEvent = 'hsdesk-profile-settings-updated';

export interface ActiveTenantPackage {
  code: string;
  name: string;
  validFrom?: string;
  validTo?: string;
  features: string[];
}

export interface ChangePasswordPayload {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
}

export type ActivityFeedCategory =
  | 'notification'
  | 'change'
  | 'comment'
  | 'time'
  | 'sms'
  | 'email'
  | 'message';

export interface ActivityFeedItem {
  id: string;
  category: ActivityFeedCategory;
  title: string;
  description: string;
  occurredAt: string;
  url?: string;
  entityType?: string;
  entityId?: string;
  operation?: string;
  status?: string;
  isRead?: boolean;
  notificationId?: string;
}

export interface ActivityFeedPage {
  items: ActivityFeedItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface ActivityFeedQuery {
  pageNumber: number;
  pageSize: number;
  categories?: ActivityFeedCategory[];
  search?: string;
}

export const defaultProfileSettings: ProfileSettingsResponse = {
  isMasterUser: false,
  isFullWidth: false,
  defaultView: 'orders',
  density: 'comfortable',
  language: 'pl',
  timezone: 'Europe/Warsaw',
  dateFormat: 'dd.MM.yyyy',
  availability: 'available',
  defaultPositionId: undefined,
  quickActions: ['new-order', 'new-client', 'sms-reminder'],
  emailNotifications: true,
  smsNotifications: true,
  pushNotifications: true,
  newOrderNotification: true,
  commentNotification: true,
  statusNotification: true,
  reminderNotification: true,
  newTenantEmailNotification: true,
  newTenantSmsNotification: false,
  newTenantInAppNotification: true,
  tenantUserEmailNotification: false,
  tenantUserSmsNotification: false,
  tenantUserInAppNotification: true,
  tenantProblemEmailNotification: true,
  tenantProblemSmsNotification: false,
  tenantProblemInAppNotification: true,
  dailySummary: false,
  showPhoneToTeam: true,
  showAvailability: true,
  requireConfirmForStatusChange: true,
  compactOrderCards: false,
  autoAssignCreatedOrders: true,
  panelInterfaceStyle: 'simple',
  panelThemeMode: 'light',
  panelPrimaryColor: '#4F46E5',
  panelBackgroundColor: '#F4F7FB',
  panelSurfaceColor: '#FFFFFF',
  panelTextColor: '#172033',
  panelSidebarColor: '#111827'
};

function normalizeList<T>(responseData: unknown): T[] {
  if (Array.isArray(responseData)) {
    return responseData as T[];
  }

  const wrapped = responseData as { data?: unknown; items?: unknown } | null;

  if (Array.isArray(wrapped?.data)) {
    return wrapped.data as T[];
  }

  if (Array.isArray(wrapped?.items)) {
    return wrapped.items as T[];
  }

  return [];
}

function normalizeSettings(data: Partial<ProfileSettingsResponse> | null) {
  return {
    ...defaultProfileSettings,
    ...(data ?? {}),
    quickActions: data?.quickActions ?? defaultProfileSettings.quickActions
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function mapProfileRole(value: unknown): UserProfileRoleDto | null {
  if (typeof value === 'string') {
    return {
      roleId: value,
      roleName: value,
      enabled: true
    };
  }

  if (!isRecord(value)) {
    return null;
  }

  const roleId = String(value.roleId ?? value.id ?? value.name ?? '').trim();
  const roleName = String(
    value.roleName ?? value.name ?? value.displayName ?? roleId
  ).trim();

  if (!roleId && !roleName) {
    return null;
  }

  return {
    roleId: roleId || roleName,
    roleName: roleName || roleId,
    description:
      typeof value.description === 'string' ? value.description : undefined,
    enabled: typeof value.enabled === 'boolean' ? value.enabled : true
  };
}

function normalizeProfileRoles(data: unknown): UserProfileRoleDto[] {
  if (!isRecord(data)) {
    return [];
  }

  const rawRoles =
    data.roles ?? data.userRoles ?? data.assignedRoles ?? data.roleNames ?? [];

  return Array.isArray(rawRoles)
    ? rawRoles
        .map(mapProfileRole)
        .filter((role): role is UserProfileRoleDto => Boolean(role))
    : [];
}

function normalizeProfile(data: UserProfileDto): UserProfileDto {
  return {
    ...data,
    roles: normalizeProfileRoles(data)
  };
}

export async function getProfile(
  axiosInstance: AxiosInstance
): Promise<UserProfileDto> {
  const response = await axiosInstance.get<UserProfileDto>(
    '/api/personal/profile'
  );

  return normalizeProfile(response.data);
}

export async function updateProfile(
  axiosInstance: AxiosInstance,
  profile: UpdateProfileRequest
) {
  await axiosInstance.put('/api/personal/profile', profile);
}

export async function getProfileSettings(
  axiosInstance: AxiosInstance
): Promise<ProfileSettingsResponse> {
  const response = await axiosInstance.get<Partial<ProfileSettingsResponse>>(
    '/api/personal/profile-settings'
  );

  return normalizeSettings(response.data);
}

export async function getActiveTenantPackage(
  axiosInstance: AxiosInstance
): Promise<ActiveTenantPackage | null> {
  const response = await axiosInstance.get<ActiveTenantPackage | null>(
    '/api/personal/package'
  );

  if (!response.data) return null;

  return {
    ...response.data,
    features: Array.isArray(response.data.features)
      ? response.data.features
      : []
  };
}

export async function updateProfileSettings(
  axiosInstance: AxiosInstance,
  settings: ProfileSettingsResponse,
  notifyProfileSettingsUpdated = true
): Promise<ProfileSettingsResponse> {
  const response = await axiosInstance.put<Partial<ProfileSettingsResponse>>(
    '/api/personal/profile-settings',
    settings
  );

  const updatedSettings = normalizeSettings(response.data ?? settings);

  if (notifyProfileSettingsUpdated && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(profileSettingsUpdatedEvent, {
        detail: updatedSettings
      })
    );
  }

  return updatedSettings;
}

export async function updateProfileSetting(
  axiosInstance: AxiosInstance,
  setting: Partial<ProfileSettingsResponse>,
  notifyProfileSettingsUpdated = true
): Promise<ProfileSettingsResponse> {
  const currentSettings = await getProfileSettings(axiosInstance);

  return updateProfileSettings(
    axiosInstance,
    {
      ...currentSettings,
      ...setting
    },
    notifyProfileSettingsUpdated
  );
}

export async function getDepartments(
  axiosInstance: AxiosInstance
): Promise<DepartmentDto[]> {
  const response = await axiosInstance.get('/api/v1/departments');

  return normalizeList<DepartmentDto>(response.data);
}

export async function getDepartmentTeams(
  axiosInstance: AxiosInstance,
  departmentId: string
): Promise<TeamDto[]> {
  if (!departmentId) {
    return [];
  }

  const response = await axiosInstance.get(
    `/api/v1/departments/${encodeURIComponent(departmentId)}/teams`
  );

  return normalizeList<TeamDto>(response.data);
}

export async function getPositions(
  axiosInstance: AxiosInstance
): Promise<PositionDto[]> {
  const response = await axiosInstance.get('/api/v1/positions');

  return normalizeList<PositionDto>(response.data);
}

export async function changePassword(
  axiosInstance: AxiosInstance,
  values: ChangePasswordPayload
) {
  const client = new PersonalClient(config.apiUrl, axiosInstance);

  await client.changePassword(new ChangePasswordRequest(values));
}

export async function getMyActivityFeed(
  axiosInstance: AxiosInstance,
  query: ActivityFeedQuery
): Promise<ActivityFeedPage> {
  const response = await axiosInstance.get('/api/personal/activity', {
    params: {
      pageNumber: query.pageNumber,
      pageSize: query.pageSize,
      categories: query.categories?.join(','),
      search: query.search?.trim() || undefined
    }
  });
  const payload = response.data as {
    data?: ActivityFeedItem[];
    items?: ActivityFeedItem[];
    totalCount?: number;
    currentPage?: number;
    pageNumber?: number;
    pageSize?: number;
  };

  return {
    items: payload.items ?? payload.data ?? [],
    totalCount: payload.totalCount ?? 0,
    pageNumber: payload.pageNumber ?? payload.currentPage ?? query.pageNumber,
    pageSize: payload.pageSize ?? query.pageSize
  };
}

function readApiErrorMessage(error: unknown) {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const data = error.response?.data as
    | {
        messages?: string[];
        supportMessage?: string;
        title?: string;
        detail?: string;
      }
    | undefined;

  return (
    data?.messages?.[0] ||
    data?.detail ||
    data?.title ||
    data?.supportMessage ||
    error.message
  );
}

export function getProfileApiErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    return 'Nie udało się wykonać operacji profilu.';
  }

  const apiMessage = readApiErrorMessage(error);

  if (error.response?.status === 404) {
    return apiMessage || 'Nie znaleziono profilu zalogowanego użytkownika.';
  }

  if (error.response?.status === 405) {
    return (
      apiMessage ||
      'Endpoint profilu istnieje, ale ta operacja używa metody HTTP nieobsługiwanej przez API.'
    );
  }

  return apiMessage || 'Nie udało się wykonać operacji profilu.';
}
